// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Wrench, LogOut, Lock, Settings, FolderOpen, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function SelectAreaPage() {
  const navigate = useNavigate();
  const { user, logout, hasPermission, isAdmin } = useAuth();

  const handleSelectArea = (area) => {
    if (!hasPermission(area)) {
      return;
    }
    localStorage.setItem('selectedArea', area);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const canAccessCongregacao = hasPermission('congregacao');
  const canAccessManutencao = hasPermission('manutencao');
  const noPermissions = !canAccessCongregacao && !canAccessManutencao;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Header with glassmorphism */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="JW Donate Audit Logo" 
              className="w-12 h-12 object-contain"
            />
            <span className="font-extrabold text-xl tracking-tight text-slate-900 font-heading">
              JW Donate Audit
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-semibold text-xs">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium text-slate-700">{user?.name?.split(' ')[0]}</span>
              {isAdmin() && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 ml-1">
                  Admin
                </Badge>
              )}
            </div>
            {isAdmin() && (
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')} className="rounded-xl hidden sm:flex">
                <Settings className="w-4 h-4 mr-2" />
                Gerenciar
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-xl text-slate-600 hover:text-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl animate-fade-in">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-3 font-heading">
              Selecione a Área
            </h1>
            <p className="text-slate-500 text-base">
              Escolha qual seção de contas você deseja acessar
            </p>
          </div>

          {noPermissions ? (
            <Card className="max-w-md mx-auto border-0 shadow-xl">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-slate-400" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3 font-heading">Sem Permissões</h2>
                <p className="text-slate-500 mb-6 leading-relaxed">
                  Você ainda não tem permissão para acessar nenhuma área.
                  Entre em contato com um administrador para solicitar acesso.
                </p>
                <Button variant="outline" onClick={handleLogout} className="rounded-xl">
                  Voltar ao Login
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contas da Congregação */}
              <Card 
                className={`transition-all duration-300 border-0 shadow-card overflow-hidden ${
                  canAccessCongregacao 
                    ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 group' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => canAccessCongregacao && handleSelectArea('congregacao')}
                data-testid="select-congregacao"
              >
                <div className={`h-2 w-full ${canAccessCongregacao ? 'bg-congregation' : 'bg-slate-200'}`} />
                <CardContent className="p-8 text-center relative">
                  {!canAccessCongregacao && (
                    <div className="absolute top-4 right-4">
                      <Lock className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 ${
                    canAccessCongregacao ? 'bg-congregation-soft group-hover:bg-congregation/10 group-hover:scale-110' : 'bg-slate-100'
                  }`}>
                    <BookOpen className={`w-10 h-10 ${canAccessCongregacao ? 'text-congregation' : 'text-slate-400'}`} />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2 font-heading">
                    Contas da Congregação
                  </h2>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {canAccessCongregacao 
                      ? 'Acesse os documentos e relatórios financeiros da congregação'
                      : 'Você não tem permissão para esta área'}
                  </p>
                  {canAccessCongregacao && (
                    <div className="mt-6">
                      <span className="inline-flex items-center px-5 py-2.5 rounded-xl bg-congregation-soft text-congregation text-sm font-semibold group-hover:bg-congregation group-hover:text-white transition-all duration-300">
                        Acessar
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Contas da Manutenção */}
              <Card 
                className={`transition-all duration-300 border-0 shadow-card overflow-hidden ${
                  canAccessManutencao 
                    ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 group' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
                onClick={() => canAccessManutencao && handleSelectArea('manutencao')}
                data-testid="select-manutencao"
              >
                <div className={`h-2 w-full ${canAccessManutencao ? 'bg-maintenance' : 'bg-slate-200'}`} />
                <CardContent className="p-8 text-center relative">
                  {!canAccessManutencao && (
                    <div className="absolute top-4 right-4">
                      <Lock className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 ${
                    canAccessManutencao ? 'bg-maintenance-soft group-hover:bg-maintenance/10 group-hover:scale-110' : 'bg-slate-100'
                  }`}>
                    <Wrench className={`w-10 h-10 ${canAccessManutencao ? 'text-maintenance' : 'text-slate-400'}`} />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2 font-heading">
                    Contas da Manutenção
                  </h2>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {canAccessManutencao 
                      ? 'Acesse os documentos e relatórios de manutenção'
                      : 'Você não tem permissão para esta área'}
                  </p>
                  {canAccessManutencao && (
                    <div className="mt-6">
                      <span className="inline-flex items-center px-5 py-2.5 rounded-xl bg-maintenance-soft text-maintenance text-sm font-semibold group-hover:bg-maintenance group-hover:text-white transition-all duration-300">
                        Acessar
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/50 border-t border-slate-200/50 px-6 py-5">
        <div className="max-w-4xl mx-auto text-center text-sm text-slate-400">
          Sistema de Auditoria e Gestão de Documentos
        </div>
      </footer>
    </div>
  );
}
